import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import IncidentCard from '../IncidentCard';
import { calculateRiskScore } from '@/utils/riskScoring';

/**
 * Real-Time Alerts View Component for SOC Dashboard
 * Displays real-time security alerts and recent incidents
 * 
 * @param {Object} props - Component props
 * @param {Object} props.data - Real-time alerts data
 * @param {Function} props.onRefresh - Callback to refresh alerts
 */
const RealTimeAlertsView = ({ data, onRefresh }) => {
  const [lastUpdate, setLastUpdate] = useState(() => new Date());

  useEffect(() => {
    // Update last update time whenever data changes (async pattern)
    const timer = setTimeout(() => {
      setLastUpdate(new Date());
    }, 0);
    return () => clearTimeout(timer);
  }, [data]);

  if (!data) {
    return (
      <div className="soc-view real-time-alerts-view">
        <h2 className="view-title">Real-Time Security Alerts</h2>
        <p className="no-data-message">Loading real-time alerts...</p>
      </div>
    );
  }

  // Get recent alerts sorted by timestamp
  const recentAlerts = data.recentAlerts || [];
  const criticalAlerts = recentAlerts.filter(alert => alert.severity === 'CRITICAL' || alert.severity === 'High');
  const highAlerts = recentAlerts.filter(alert => alert.severity === 'HIGH' || alert.severity === 'Medium');

  // Calculate overall alert risk score
  const overallAlertScore = calculateRiskScore({
    fileCount: recentAlerts.length,
    failedAttempts: criticalAlerts.length * 2,
    uniqueIPCount: data.uniqueSourceIPs || 0
  });

  /**
   * Formats alert timestamp to relative time (e.g., "2 minutes ago")
   */
  const formatRelativeTime = (timestamp) => {
    const now = new Date();
    const alertTime = new Date(timestamp);
    const diffMs = now - alertTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return alertTime.toLocaleString();
  };

  /**
   * Gets severity color based on severity level
   */
  const getSeverityColor = (severity) => {
    switch (severity?.toUpperCase()) {
      case 'CRITICAL':
      case 'HIGH':
        return '#dc3545';
      case 'MEDIUM':
        return '#fd7e14';
      case 'LOW':
        return '#ffc107';
      default:
        return '#6c757d';
    }
  };

  return (
    <div className="soc-view real-time-alerts-view">
      <div className="view-header-with-refresh">
        <h2 className="view-title">Real-Time Security Alerts</h2>
        <button className="refresh-alerts-btn" onClick={onRefresh} aria-label="Refresh alerts">
          ↻ Refresh
        </button>
      </div>

      {/* Summary Header */}
      <div className="view-summary-header">
        <div className="summary-stat">
          <span className="summary-label">Active Alerts</span>
          <span className="summary-value">{recentAlerts.length}</span>
        </div>
        <div className="summary-stat critical">
          <span className="summary-label">Critical</span>
          <span className="summary-value">{criticalAlerts.length}</span>
        </div>
        <div className="summary-stat high">
          <span className="summary-label">High Priority</span>
          <span className="summary-value">{highAlerts.length}</span>
        </div>
        <div className="summary-stat">
          <span className="summary-label">Last Update</span>
          <span className="summary-value-small">{lastUpdate.toLocaleTimeString()}</span>
        </div>
      </div>

      {/* Overall Alert Status Card */}
      <div className="alert-status-card">
        <IncidentCard
          title="Current Alert Status"
          riskScore={overallAlertScore}
          data={{
            count: recentAlerts.length,
            criticalAlerts: criticalAlerts.length,
            highAlerts: highAlerts.length,
            uniqueSources: data.uniqueSourceIPs || 0,
            affectedSystems: data.affectedSystems || 0
          }}
          incidentType="AdvancedHunting"
          expandable={false}
        />
      </div>

      {/* Real-Time Alert Feed */}
      <div className="view-details-section alert-feed-section">
        <h3>Recent Alert Feed</h3>
        <div className="alert-feed">
          {recentAlerts.length > 0 ? (
            <div className="alert-feed-list">
              {recentAlerts.slice(0, 10).map((alert, idx) => (
                <div key={idx} className="alert-feed-item" style={{ borderLeftColor: getSeverityColor(alert.severity) }}>
                  <div className="alert-header-row">
                    <span className="alert-severity" style={{ color: getSeverityColor(alert.severity) }}>
                      {alert.severity || 'UNKNOWN'}
                    </span>
                    <span className="alert-timestamp">{formatRelativeTime(alert.timestamp)}</span>
                  </div>
                  <div className="alert-title-row">
                    <strong>{alert.title || 'Untitled Alert'}</strong>
                  </div>
                  <div className="alert-details-row">
                    <span className="alert-category">{alert.category || 'General'}</span>
                    {alert.source && <span className="alert-source">Source: {alert.source}</span>}
                    {alert.user && <span className="alert-user">User: {alert.user}</span>}
                  </div>
                  {alert.description && (
                    <div className="alert-description">
                      {alert.description}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="no-alerts-message">
              <p>✓ No active alerts at this time</p>
              <p className="sub-message">All systems operating normally</p>
            </div>
          )}
        </div>
      </div>

      {/* Alert Categories Breakdown */}
      <div className="view-details-section">
        <h3>Alerts by Category</h3>
        <div className="alert-categories-grid">
          {data.alertsByCategory && Object.keys(data.alertsByCategory).length > 0 ? (
            Object.entries(data.alertsByCategory).map(([category, count]) => (
              <div key={category} className="category-stat">
                <span className="category-label">{category}</span>
                <span className="category-count">{count}</span>
              </div>
            ))
          ) : (
            <p className="no-data-message">No category data available</p>
          )}
        </div>
      </div>
    </div>
  );
};

RealTimeAlertsView.propTypes = {
  data: PropTypes.object,
  onRefresh: PropTypes.func
};

RealTimeAlertsView.defaultProps = {
  data: null,
  onRefresh: () => {}
};

export default RealTimeAlertsView;
